import { cn } from '@my/ui';
import { ChevronDown, ChevronUp, Plus, X } from 'lucide-react';
import { useRef, useState } from 'react';
import {
  capacityOf,
  DEFAULT_SUBCONTRACTOR_MAX,
  isFleet,
  POOL_LABEL,
  TACHO_STATUS_ACCENT,
  TACHO_STATUS_LABEL,
  type PoolId,
  type ResourceCardVM,
} from '../lib/planResources';
import { CapBadge, SectionBand } from './PlanShared';

/** Chips shown per card before collapsing the rest into "+N". */
const MAX_CAPS = 5;

interface ResourceStripProps {
  byPool: Record<PoolId, ResourceCardVM[]>;
  visiblePools: PoolId[];
  activePool: PoolId | undefined;
  onPoolChange: (pool: PoolId) => void;
  isLoading: boolean;
  isError: boolean;
  /** Opens the (still to be defined) vehicle picker for a driver. */
  onSelectVehicle: (resourceId: string) => void;
  /** Persists a subcontractor's concurrency ceiling (`Party.maxConcurrentAssignments`). */
  onAssignmentCapChange: (subcontractorId: string, next: number) => void;
  /** Opens the add-subcontractor-trailer form. */
  onAddSubcontractorTrailer: () => void;
  /** Removes a subcontractor trailer from the pool. */
  onRemoveSubcontractorTrailer: (trailerId: string, unitName: string) => void;
  /**
   * Resource / subcontractor-trailer id → the jobs it's currently on. Drives the
   * "on JOB-x" line so a planner can see a trailer is already committed.
   */
  jobsByResourceId: Map<string, string[]>;
  /** Assigns a card to the movement currently selected in the Job Queue. */
  onAssign: (card: ResourceCardVM) => void;
  /** Drag lifecycle — the page holds the dragged card so the queue can drop it. */
  onDragStartCard: (card: ResourceCardVM) => void;
  onDragEndCard: () => void;
  /** True while an assignment write is in flight — freezes the Assign buttons. */
  assigning: boolean;
  /** Folded away to a small tab at the bottom: no bar, no cards. */
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
}

/**
 * Resource strip — the bottom band of the planning cockpit. Tabs across the
 * available pools: rostered drivers, drivers not yet on the roster, trailers,
 * vehicles (trucks + rigids) and subcontractors. A pool with nothing in it is
 * hidden, so an org with every driver rostered never sees a "Drivers" tab (and
 * vice versa).
 *
 * Purely presentational — the pools (and the depot filtering behind them) come
 * from `usePlanResources` on the page, which the Gantt shares so both always show
 * the same resources.
 */
export function ResourceStrip({
  byPool,
  visiblePools,
  activePool,
  onPoolChange,
  isLoading,
  isError,
  onSelectVehicle,
  onAssignmentCapChange,
  onAddSubcontractorTrailer,
  onRemoveSubcontractorTrailer,
  jobsByResourceId,
  onAssign,
  onDragStartCard,
  onDragEndCard,
  assigning,
  collapsed,
  onCollapsedChange,
}: ResourceStripProps) {
  const cards = activePool ? byPool[activePool] : [];
  /**
   * The Add card IS the content of an empty subcontractor-trailers pool, so the
   * usual "nothing available" message must stand aside for it — otherwise the
   * only route to creating the first unit is behind an empty state.
   */
  const showAddCard = activePool === 'subcontractorTrailers';

  // Folded: no bar at all — just a small handle on the bottom edge of the
  // screen, tucked under the section above, so the Job Queue gets every pixel
  // back. (The page drops its bottom padding while folded.)
  if (collapsed) {
    return (
      <div className="-mt-3 flex shrink-0">
        <button
          type="button"
          onClick={() => onCollapsedChange(false)}
          aria-expanded={false}
          aria-label="Show resources"
          title="Show resources"
          className="flex h-4 items-center rounded-t-md bg-slate-700 px-2.5 text-slate-300 transition-colors hover:bg-slate-600 hover:text-white"
        >
          <ChevronUp className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  const tabStrip = (
    <div className="flex flex-wrap items-center gap-1">
      <button
        type="button"
        onClick={() => onCollapsedChange(true)}
        aria-expanded
        aria-label="Hide resources"
        title="Hide resources"
        className="-my-0.5 mr-1 rounded p-0.5 text-slate-300 transition-colors hover:bg-slate-600 hover:text-white"
      >
        <ChevronDown className="h-3.5 w-3.5" />
      </button>
      {visiblePools.map((id) => (
        <button
          key={id}
          type="button"
          onClick={() => onPoolChange(id)}
          aria-pressed={id === activePool}
          // -my-0.5: the pill reaches into the bar's padding instead of making
          // the bar taller, so this bar is the same height as every other one.
          className={cn(
            '-my-0.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide transition-colors',
            id === activePool
              ? 'bg-amber-500 text-white'
              : 'text-slate-300 hover:bg-slate-600 hover:text-white',
          )}
        >
          {POOL_LABEL[id]}
          <span className="ml-1 opacity-70">{byPool[id].length}</span>
        </button>
      ))}
      {visiblePools.length === 0 && (
        <span className="text-[11px] font-semibold uppercase tracking-wide text-white">
          Resources
        </span>
      )}
    </div>
  );

  return (
    <SectionBand
      label="Resources"
      tabs={tabStrip}
      right={activePool ? <span>{cards.length} available</span> : undefined}
      className="shrink-0"
    >
      {/*
        Fixed height, and `scrollbar-gutter: stable` so the horizontal scrollbar's
        space is reserved even on tabs that don't need it (Vehicles, Subcontractors).
        Together they keep every tab — and every card, which stretches to fill —
        exactly the same size instead of resizing as you switch pools.
      */}
      <div className="h-72 shrink-0">
        {isError ? (
          <div className="p-4 text-xs text-red-600">Couldn&apos;t load resources.</div>
        ) : isLoading ? (
          <div className="p-4 text-xs text-slate-400">Loading resources…</div>
        ) : cards.length === 0 && !showAddCard ? (
          <div className="p-4 text-xs text-slate-400">
            Nothing available at this depot — try another depot or clear the filter.
          </div>
        ) : (
          <div
            className="flex h-full items-stretch gap-2 overflow-x-auto p-2"
            style={{ scrollbarGutter: 'stable' }}
          >
            {showAddCard && (
              <AddSubcontractorTrailerCard onClick={onAddSubcontractorTrailer} />
            )}
            {cards.map((c) => (
              <ResourceCard
                key={c.id}
                c={c}
                onSelectVehicle={onSelectVehicle}
                onAssignmentCapChange={onAssignmentCapChange}
                onRemove={onRemoveSubcontractorTrailer}
                jobs={jobsByResourceId.get(c.id) ?? []}
                onAssign={onAssign}
                onDragStartCard={onDragStartCard}
                onDragEndCard={onDragEndCard}
                assigning={assigning}
              />
            ))}
          </div>
        )}
      </div>
    </SectionBand>
  );
}

/**
 * The driver's linked vehicle: registration on the left, live tacho status on
 * the right. With nothing linked the line turns into an amber "No vehicle"
 * button — the planner's cue that this driver can't be dispatched yet — and the
 * status is omitted (there's no tacho to report without a vehicle).
 */
function VehicleLine({
  c,
  onSelectVehicle,
}: {
  c: ResourceCardVM;
  onSelectVehicle: (resourceId: string) => void;
}) {
  if (!c.linkedVehicle) {
    return (
      <button
        type="button"
        onClick={() => onSelectVehicle(c.id)}
        className="text-[11px] font-medium text-amber-600 underline decoration-dotted underline-offset-2 hover:text-amber-700"
      >
        No vehicle
      </button>
    );
  }

  const { reg, tachoStatus } = c.linkedVehicle;
  return (
    <div className="flex items-baseline justify-between gap-1">
      <button
        type="button"
        onClick={() => onSelectVehicle(c.id)}
        className="truncate font-mono text-[11px] text-blue-600 hover:underline"
        title={`${reg} — change vehicle`}
      >
        {reg}
      </button>
      <span className={cn('shrink-0 text-[10px] font-medium', TACHO_STATUS_ACCENT[tachoStatus])}>
        {TACHO_STATUS_LABEL[tachoStatus]}
      </span>
    </div>
  );
}

/**
 * Placeholder card that opens the add-subcontractor-trailer form. Matches the
 * real cards' footprint exactly so the row doesn't jog, and leads the pool —
 * it's the only way to create one, so it shouldn't be at the end of 30 units.
 */
function AddSubcontractorTrailerCard({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex min-h-64 w-48 shrink-0 flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed border-slate-300 bg-white p-2.5 text-xs text-slate-500 transition-colors hover:border-amber-400 hover:text-amber-600"
    >
      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100">
        <Plus className="h-4 w-4" />
      </span>
      <span className="text-center font-medium leading-tight">Add subcontractor trailer</span>
    </button>
  );
}

/**
 * Subcontractor capacity: how much of their assignment cap is taken, and the
 * cap itself as an editable field.
 *
 * <p>
 * Assignment doesn't exist yet, so `assigned` and `accepted` are always 0 and
 * the bar reads empty — the meter is here so the cap has somewhere to live and
 * the layout is settled before the real counts arrive. The cap is in-memory
 * only (see `PlanPage`), so it resets on reload.
 * </p>
 */
function CapacityMeter({
  assigned,
  accepted,
  max,
  onMaxChange,
}: {
  assigned: number;
  accepted: number;
  max: number;
  onMaxChange: (next: number) => void;
}) {
  const cap = capacityOf(assigned, accepted, max);
  return (
    <div className="space-y-1">
      <div className="flex h-1.5 overflow-hidden rounded-full bg-slate-100">
        <div className="h-full bg-amber-400" style={{ width: `${cap.assignedPct}%` }} />
        <div className="h-full bg-emerald-500" style={{ width: `${cap.acceptedPct}%` }} />
      </div>
      <div className="flex flex-wrap gap-x-1 text-[10px] leading-tight">
        <span className="font-medium text-amber-600">{cap.assigned} assigned</span>
        <span className="font-medium text-emerald-600">{cap.accepted} accepted</span>
        {/* Over-cap is possible — the cap is a planner's guess, so say so
            rather than quietly showing "0 free". */}
        {cap.over > 0 ? (
          <span className="font-medium text-red-600">{cap.over} over</span>
        ) : (
          <span className="text-slate-400">{cap.free} free</span>
        )}
      </div>
      <div className="flex items-center justify-between gap-1">
        <span className="text-[10px] font-medium text-slate-500">Max</span>
        <input
          type="number"
          min={0}
          value={max}
          onChange={(e) => {
            const next = Number(e.target.value);
            // An empty field parses to NaN; keep the previous cap rather than
            // letting the meter divide by nothing.
            if (Number.isFinite(next)) onMaxChange(Math.max(0, Math.trunc(next)));
          }}
          className="w-12 rounded border border-slate-200 px-1 py-0.5 text-right text-[11px] text-slate-700 focus:border-amber-400 focus:outline-none"
        />
      </div>
    </div>
  );
}

/**
 * One card. Fixed width, and it stretches to the strip's fixed height, so every
 * pool renders the same size card. The identity block hugs the top, the chip
 * area absorbs the slack, and the action is pinned to the bottom rather than
 * padded out with fake driver stats.
 */
function ResourceCard({
  c,
  onSelectVehicle,
  onAssignmentCapChange,
  onRemove,
  jobs,
  onAssign,
  onDragStartCard,
  onDragEndCard,
  assigning,
}: {
  c: ResourceCardVM;
  onSelectVehicle: (resourceId: string) => void;
  onAssignmentCapChange: (subcontractorId: string, next: number) => void;
  onRemove: (trailerId: string, unitName: string) => void;
  /** Jobs this resource is currently on, newest-first as the queue orders them. */
  jobs: string[];
  onAssign: (card: ResourceCardVM) => void;
  onDragStartCard: (card: ResourceCardVM) => void;
  onDragEndCard: () => void;
  assigning: boolean;
}) {
  /**
   * HTML5 drag is armed on mousedown rather than left permanently on, because a
   * `draggable` ancestor stops the Max number field and the selects inside the
   * card from taking a cursor or a text selection. Interactive descendants are
   * excluded so pressing Assign or editing the cap never starts a drag.
   */
  const [dragArmed, setDragArmed] = useState(false);
  /**
   * Compact stand-in used as the drag image. The browser snapshots whatever
   * element you hand `setDragImage`, and the default — the whole 192×256 card —
   * is far too big to aim with: it covers the row you're trying to drop on.
   * Rendered off-screen because the element has to be in the document and
   * painted for the snapshot to work; `display:none` would produce nothing.
   */
  const dragGhostRef = useRef<HTMLDivElement>(null);

  return (
    <div
      draggable={dragArmed}
      onMouseDown={(e) => {
        const target = e.target as HTMLElement;
        if (target.closest('input, select, button, a, textarea')) return;
        setDragArmed(true);
      }}
      onMouseUp={() => setDragArmed(false)}
      onDragStart={(e) => {
        // Firefox ignores a drag with no payload.
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', c.id);
        // Offset a little from the cursor so the label sits beside the pointer
        // rather than under it, keeping the drop target visible.
        if (dragGhostRef.current) e.dataTransfer.setDragImage(dragGhostRef.current, 12, 14);
        onDragStartCard(c);
      }}
      onDragEnd={() => {
        setDragArmed(false);
        onDragEndCard();
      }}
      className={cn(
        'flex min-h-64 w-48 shrink-0 flex-col gap-2 rounded-md border p-2.5 text-xs',
        'border-slate-200 bg-white',
        dragArmed && 'cursor-grab',
        !c.available && 'opacity-60',
      )}
    >
      {/*
        Drag image. Off-screen but painted, so the browser can snapshot it.
        Shows exactly what the drop will assign — a driver names their linked
        vehicle too, because that comes along with them.
      */}
      <div
        ref={dragGhostRef}
        aria-hidden
        className="pointer-events-none fixed left-[-9999px] top-0 inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-800 shadow-md"
      >
        <span>{c.title}</span>
        {c.linkedVehicle && (
          <>
            <span className="text-slate-300">·</span>
            <span className="font-mono text-slate-500">{c.linkedVehicle.reg}</span>
          </>
        )}
      </div>

      {/* Identity block sits tight to the top — name, then start · location on
          the very next line. Meta / note render only when present; equal card
          height comes from the strip's fixed height + stretch, not from padding
          these out with blank lines. */}
      <div className="space-y-0.5">
        {/* Name, with the channel badge pinned top-right. The badge is
            shrink-0 so a long party name truncates instead of squeezing it. */}
        <div className="flex items-start justify-between gap-1">
          <div className="truncate font-semibold text-slate-800" title={c.title}>
            {c.title}
          </div>
          {c.topBadge && (
            <span
              className={cn(
                'shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium',
                c.topBadge.accent,
              )}
            >
              {c.topBadge.label}
            </span>
          )}
          {/* Removing a unit is only meaningful for a subcontractor's trailer —
              ours are managed on the Resources page. It's also the only way a
              spent drop trailer leaves the pool until jobs record which unit
              they used, so it isn't a nicety. */}
          {c.isSubcontractorUnit && (
            <button
              type="button"
              onClick={() => onRemove(c.id, c.title)}
              aria-label={`Remove ${c.title}`}
              title="Remove from the pool"
              className="shrink-0 rounded-full p-0.5 text-slate-300 transition-colors hover:bg-red-50 hover:text-red-500"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        {/* On a subcontractor's trailer the second line is whose it is, so it
            gets the amber emphasis — that's the fact the planner is checking. */}
        <div
          className={cn(
            'truncate text-[11px]',
            c.isSubcontractorUnit ? 'font-medium text-amber-600' : 'text-slate-500',
          )}
          title={c.subtitle}
        >
          {c.subtitle}
        </div>
        {/* Linked vehicle — drivers only. Registration on the left, live tacho
            status on the right; when nothing is linked the whole line becomes
            the call to action to pick one. */}
        {c.showTacho && <VehicleLine c={c} onSelectVehicle={onSelectVehicle} />}
        {/* Fleet units get where they're based, then where they actually are.
            The second line is a placeholder until telematics lands — shown
            rather than hidden so the planner knows the field exists and is
            pending, the same call as the driver cards' "Tacho —". Drivers
            already carry their location in the subtitle. */}
        {isFleet(c) && (
          <>
            <div
              className="truncate text-[11px] text-slate-500"
              title={c.depotLabel ?? 'No depot assigned'}
            >
              Depot: {c.depotLabel ?? '—'}
            </div>
            <div className="truncate text-[10px] italic text-slate-400">
              Location: {c.currentLocationLabel ?? '—'}
            </div>
          </>
        )}
        {/* What this resource is currently committed to — the order number for a
            direct-dispatched job, the manifest number for a consolidated one.
            Shown on every pool: "is this one free?" is the question the strip
            exists to answer, whoever's asking. Falls back to a muted "Free" so an
            empty state reads as answered rather than as missing data. */}
        <div className="truncate text-[11px]">
          {jobs.length === 0 ? (
            <span className="text-slate-400">Free</span>
          ) : (
            <span
              className="font-medium text-blue-600"
              title={jobs.length > 1 ? jobs.join(', ') : undefined}
            >
              On {jobs[0]}
              {jobs.length > 1 && (
                <span className="ml-1 font-normal text-slate-400">+{jobs.length - 1}</span>
              )}
            </span>
          )}
        </div>
        {/* Subcontractor location: country chip + town. The chip is shrink-0
            so it survives a long city name truncating, and the full address
            hangs off the tooltip for when the town alone isn't enough. */}
        {(c.cityLabel || c.countryCode) && (
          <div
            className="flex items-center gap-1 text-[11px] text-slate-500"
            title={c.addressLabel ?? undefined}
          >
            {c.countryCode && (
              <span className="shrink-0 rounded bg-slate-100 px-1 py-0.5 text-[10px] font-semibold text-slate-600">
                {c.countryCode}
              </span>
            )}
            <span className="truncate">{c.cityLabel ?? 'No city'}</span>
          </div>
        )}
        {c.meta && (
          <div className="truncate text-[11px] text-slate-400" title={c.meta}>
            {c.meta}
          </div>
        )}
        {/* Amber is a warning here ("Not on the roster"). On a subcontractor
            unit the note is neutral detail and the coloured presence badge is
            already carrying the signal, so it stays grey rather than competing. */}
        {c.note && (
          <div
            className={cn(
              'text-[10px] font-medium',
              c.isSubcontractorUnit ? 'text-slate-500' : 'text-amber-600',
            )}
          >
            {c.note}
          </div>
        )}
      </div>

      {c.showTacho && (
        <div>
          <div className="flex items-center justify-between text-[10px] text-slate-500">
            <span>Tacho —</span>
            <span>9h</span>
          </div>
          <div className="mt-0.5 h-1.5 overflow-hidden rounded-full bg-slate-100">
            <div className="h-full rounded-full bg-slate-200" style={{ width: '0%' }} />
          </div>
        </div>
      )}

      {/*
        The chip area absorbs the card's spare height (`flex-1`) and clips rather
        than growing, so a driver with six certs is exactly as tall as one with
        none. Overflow collapses into a "+N" chip; the full list is in the tooltip.
      */}
      <div
        className="flex min-h-0 flex-1 flex-wrap content-start gap-1 overflow-hidden"
        title={c.caps.length > 0 ? c.caps.map((cap) => cap.label).join(', ') : undefined}
      >
        {/* What the unit is, ahead of what it can carry. Rigids show their
            equipment type here instead of a bare "Rigid". */}
        {c.typeChip && (
          <span
            className="inline-block rounded bg-purple-100 px-1.5 py-0.5 text-[10px] font-medium text-purple-500"
            title={c.typeChip}
          >
            {c.typeChip}
          </span>
        )}
        {c.caps.slice(0, MAX_CAPS).map((cap) => (
          <CapBadge key={cap.label} label={cap.label} variant={cap.serviceTag ? 'tag' : 'cert'} />
        ))}
        {c.caps.length > MAX_CAPS && (
          <span className="inline-block rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500">
            +{c.caps.length - MAX_CAPS}
          </span>
        )}
      </div>

      {c.showTacho && <div className="text-[10px] text-slate-500">On-time: —</div>}

      {/* Subcontractor capacity, directly above the action — the cap is what
          decides whether Assign is a reasonable thing to press. */}
      {c.assignedCount !== null && c.acceptedCount !== null && (
        <CapacityMeter
          assigned={c.assignedCount}
          accepted={c.acceptedCount}
          // Null cap = nobody has set one; show the default without pretending
          // it was chosen. Saving writes a real value.
          max={c.assignmentCap ?? DEFAULT_SUBCONTRACTOR_MAX}
          onMaxChange={(next) => onAssignmentCapChange(c.id, next)}
        />
      )}

      {/* Footer action. An inactive resource can't be dispatched at all, so that
          wins; otherwise a driver with no vehicle linked is asked to pick one
          first — assigning work to a driver who can't move isn't useful. */}
      {!c.available ? (
        <span className="mt-auto rounded-md border border-red-200 bg-red-50 px-2 py-1 text-center text-[10px] font-medium text-red-500">
          NOT AVAILABLE
        </span>
      ) : c.showTacho && !c.linkedVehicle ? (
        <button
          type="button"
          onClick={() => onSelectVehicle(c.id)}
          className="mt-auto rounded-md bg-amber-500 px-2 py-1 text-[11px] font-medium text-white hover:bg-amber-600"
        >
          Choose vehicle
        </button>
      ) : (
        <button
          type="button"
          onClick={() => onAssign(c)}
          disabled={assigning}
          className="mt-auto rounded-md bg-emerald-600 px-2 py-1 text-[11px] font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
        >
          Assign →
        </button>
      )}
    </div>
  );
}
